import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import allRoutes from 'client/constants/allRoutes';

// ─── Demo Popup (shown when demo user tries a write action) ──────────────────
const DemoPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15h.01M12 9v3m-7.938 6h15.876c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L2.34 16.5c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        <h3 className="text-lg font-bold text-gray-900 mb-2">Demo Mode</h3>
        <p className="text-sm text-gray-500 mb-6 leading-relaxed">
          You are exploring Vistaar with sample data. Changes are not saved in demo mode. Create your clinic account in 30 seconds to start using the full system.
        </p>

        <div className="flex flex-col gap-2">
          <button
            onClick={() => { onClose(); navigate(allRoutes.signup || '/signup'); }}
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-brand-500 hover:bg-brand-600 transition-colors"
          >
            Start Free Trial
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors"
          >
            Continue Exploring
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Banner + Popup Controller ──────────────────────────────────────────
const DemoBanner = () => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const [popupOpen, setPopupOpen] = useState(false);
  const isDemo = user?.is_demo === 1 || user?.is_demo === true;

  // Listen for demo block events from API interceptor
  const handleDemoBlock = useCallback(() => {
    setPopupOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener('demo:blocked', handleDemoBlock);
    return () => window.removeEventListener('demo:blocked', handleDemoBlock);
  }, [handleDemoBlock]);

  if (!isDemo) return null;

  return (
    <>
      {/* Persistent top banner — no dismiss */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-3 z-50 shrink-0">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white rounded">Demo</span>
          <span className="text-xs font-medium text-amber-800">
            You are exploring Vistaar with sample data. Changes are disabled.
          </span>
        </div>
        <button
          onClick={() => navigate(allRoutes.signup || '/signup')}
          className="px-3 py-1 text-[11px] font-bold bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors flex-shrink-0"
        >
          Create Free Account
        </button>
      </div>

      {/* Demo action blocked popup */}
      <DemoPopup isOpen={popupOpen} onClose={() => setPopupOpen(false)} />
    </>
  );
};

export default DemoBanner;
